// components/QuickMessageActions.js - Fixed version with scroll action and confirmation
import React, { useState, useEffect } from 'react';
import { 
    Box, 
    HStack, 
    IconButton, 
    useClipboard, 
    useToast 
} from '@chakra-ui/react';
import { CopyIcon, CheckIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FaCopy } from 'react-icons/fa';
import ConfirmationModal from './ConfirmationModal';

const QuickMessageActions = ({
    message,
    messageIndex,
    onDuplicateConversation,
    isBotMessage,
    messageBoxRef,
    onScrollToBottom // New prop to handle scroll to bottom
}) => {
    const { hasCopied, onCopy } = useClipboard(message?.text || '');
    const toast = useToast();
    const [coords, setCoords] = useState(null);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);

    // Calculate position on mount and on scroll/resize
    useEffect(() => {
        const updateCoords = () => {
            if (!messageBoxRef.current) return;
            const rect = messageBoxRef.current.getBoundingClientRect();
            // Place actions near the top-right, but clamp to window
            const top = Math.max(12, Math.min(rect.top + 24, window.innerHeight - 56));
            const left = Math.min(rect.right - 120, window.innerWidth - 120); // Wider for 3 buttons
            setCoords({
                top,
                left,
                visible:
                    rect.bottom > 0 &&                  // not scrolled above viewport
                    rect.top < window.innerHeight        // not scrolled below viewport
            });
        };
        updateCoords();
        window.addEventListener('scroll', updateCoords, true);
        window.addEventListener('resize', updateCoords);
        return () => {
            window.removeEventListener('scroll', updateCoords, true);
            window.removeEventListener('resize', updateCoords);
        };
    }, [messageBoxRef]);

    // If message is not visible, or coords uncomputed, render nothing
    if (!coords || !coords.visible) return null;

    const handleCopy = e => {
        e.preventDefault();
        e.stopPropagation();
        onCopy();
        toast({
            title: 'Copied!',
            description: 'Message copied to clipboard',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const handleDuplicateClick = e => {
        e.preventDefault();
        e.stopPropagation();
        setShowDuplicateModal(true);
    };

    const handleConfirmDuplicate = () => {
        if (onDuplicateConversation) {
            onDuplicateConversation(messageIndex);
        }
        setShowDuplicateModal(false);
    };

    const handleScrollToBottom = e => {
        e.preventDefault();
        e.stopPropagation();
        if (onScrollToBottom) {
            onScrollToBottom();
        }
    };

    return (
        <>
            <Box
                position="fixed"
                top={`${coords.top}px`}
                left={`${coords.left}px`}
                zIndex={100}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                shadow="lg"
                p={1}
                opacity={0.97}
                display="flex"
                alignItems="center"
                pointerEvents="auto"
                cursor="default" // Override any parent cursor styles
            >
                <HStack spacing={1}>
                    {/* Copy Action */}
                    <IconButton
                        icon={hasCopied ? <CheckIcon /> : <CopyIcon />}
                        size="xs"
                        variant="solid"
                        colorScheme={hasCopied ? "green" : "gray"}
                        onClick={handleCopy}
                        aria-label="Copy message"
                        cursor="pointer" // Explicit pointer only on buttons
                    />
                    
                    {/* Duplicate Action - Only for bot messages */}
                    {isBotMessage && onDuplicateConversation && (
                        <IconButton
                            icon={<FaCopy />}
                            size="xs"
                            variant="solid"
                            colorScheme="blue"
                            onClick={handleDuplicateClick}
                            aria-label="Duplicate conversation"
                            cursor="pointer"
                        />
                    )}
                    
                    {/* Scroll to Bottom Action */}
                    <IconButton
                        icon={<ChevronDownIcon />}
                        size="xs"
                        variant="solid"
                        colorScheme="purple"
                        onClick={handleScrollToBottom}
                        aria-label="Scroll to bottom"
                        cursor="pointer"
                    />
                </HStack>
            </Box>

            {/* Duplicate Confirmation Modal */}

            <ConfirmationModal
                isOpen={showDuplicateModal}
                onClose={() => setShowDuplicateModal(false)}
                onConfirm={handleConfirmDuplicate}
                title="Duplicate Conversation"
                message="Are you sure you want to duplicate this conversation up to this message?"
                confirmButtonText="Duplicate"
                confirmButtonColorScheme="green"
            />

        </>
    );
};

export default QuickMessageActions;